import { Cancel, Edit } from '@mui/icons-material';
import {
    alpha,
    Chip,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import CancelTravelSchedule from 'Application/components/Modals/CancelTravelSchedule';
import UpdateSvData from 'Application/components/Modals/UpdateSvData';
import { AgencyLocalTravelData, AgencySvData, ScheduleStatus, UserRole } from 'Application/models';
import { addCommasToNumbers, changeDateFormat, concatenateLocalTravelDestinations, getUserRoleNameFromStorage } from 'Application/utils';
import { ThemeMode } from 'config';
import { FC, useState } from 'react';

interface PerksDataTableProps {
    perksData: AgencyLocalTravelData[];
    agencyName: string;
    pageNumber: number;
    pageSize: number;
}

export const LocalTravelDataTable: FC<PerksDataTableProps> = ({ perksData, agencyName, pageNumber, pageSize }) => {
    const [selectedSvData, setSelectedSvData] = useState<AgencySvData | null>(null);
    const [selectedSvNumber, setSelectedSvNumber] = useState<string | null>(null);

    const [openEditSvDataDialog, setOpenEditSvDataDialog] = useState<boolean>(false);
    const [openCancelTravelScheduleDialog, setOpenCancelTravelScheduleDialog] = useState<boolean>(false);

    const theme = useTheme();
    const userRole = getUserRoleNameFromStorage();

    const isUserFromAgency =
        userRole === UserRole.ROLE_AGENCY_ADMIN || userRole === UserRole.ROLE_AGENT || userRole === UserRole.ROLE_BRANCH_MANAGER;

    const handleEditSvData = (svData: AgencySvData) => {
        setSelectedSvData(svData);
        setOpenEditSvDataDialog(true);
    };

    const handleCancelTravelSchedule = (svNumber: string) => {
        setSelectedSvNumber(svNumber);
        setOpenCancelTravelScheduleDialog(true);
    };

    return (
        <TableContainer>
            <Table sx={{ minWidth: 1500 }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Agent</TableCell>
                        <TableCell>No. of passengers</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Total premium</TableCell>
                        <TableCell>Balance notes</TableCell>
                        <TableCell>SV number</TableCell>
                        <TableCell>Travelling from</TableCell>
                        <TableCell>Travelling to</TableCell>
                        <TableCell>Departure date</TableCell>
                        <TableCell>Return date</TableCell>
                        <TableCell>Schedule status</TableCell>
                        <TableCell>Created at</TableCell>
                        {isUserFromAgency && <TableCell>Actions</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {perksData.length > 0 ? (
                        perksData.map((data, index) => {
                            const concatenatedDestinations = concatenateLocalTravelDestinations(data.destination);
                            const formattedOriginValue = data.origin.split(',').length > 1 ? data.origin : data.origin.toTitle();

                            return (
                                <TableRow key={index}>
                                    <TableCell>{data.agent}</TableCell>
                                    <TableCell>{data.numOfPassengers}</TableCell>
                                    <TableCell>{data.branch}</TableCell>
                                    <TableCell>{addCommasToNumbers(data.totalPremium.toString())}</TableCell>
                                    <TableCell>{data.balanceNote}</TableCell>
                                    <TableCell>{data.svNumber}</TableCell>
                                    <TableCell>{formattedOriginValue}</TableCell>
                                    <TableCell>{concatenatedDestinations}</TableCell>
                                    <TableCell>{changeDateFormat(data.departureDate)}</TableCell>
                                    <TableCell>{changeDateFormat(data.returnDate)}</TableCell>
                                    <TableCell>
                                        {
                                            <Chip
                                                label={data.scheduleStatus.toTitle()}
                                                size="small"
                                                sx={
                                                    data.scheduleStatus === ScheduleStatus.CONFIRMED
                                                        ? {
                                                              bgcolor:
                                                                  theme.palette.mode === ThemeMode.DARK
                                                                      ? 'dark.main'
                                                                      : alpha(theme.palette.success.light, 0.6),
                                                              color: 'success.dark'
                                                          }
                                                        : {
                                                              bgcolor:
                                                                  theme.palette.mode === ThemeMode.DARK
                                                                      ? 'dark.main'
                                                                      : alpha(theme.palette.error.light, 0.6),
                                                              color: 'error.dark'
                                                          }
                                                }
                                            />
                                        }
                                    </TableCell>
                                    <TableCell>{changeDateFormat(data.createdAt)}</TableCell>
                                    {isUserFromAgency && (
                                        <TableCell>
                                            {data.scheduleStatus === ScheduleStatus.CONFIRMED && (
                                                <Stack direction="row" justifyContent="center" alignItems="center">
                                                    <Tooltip placement="top" title="Update SV data">
                                                        <IconButton
                                                            color="primary"
                                                            aria-label="edit"
                                                            size="large"
                                                            onClick={() =>
                                                                handleEditSvData({
                                                                    svNumber: data.svNumber,
                                                                    balanceNote: data.balanceNote,
                                                                    branch: data.branch,
                                                                    departureDate: data.departureDate,
                                                                    returnDate: data.returnDate,
                                                                    origin: data.origin,
                                                                    destination: data.destination,
                                                                    numOfTravellers: data.numOfPassengers
                                                                })
                                                            }
                                                        >
                                                            <Edit sx={{ fontSize: '1.1rem' }} />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip placement="top" title="Cancel travel schedule">
                                                        <IconButton
                                                            sx={{
                                                                color: 'orange.dark',
                                                                borderColor: 'orange.main',
                                                                '&:hover ': { bgcolor: 'orange.light' }
                                                            }}
                                                            aria-label="cancel"
                                                            size="large"
                                                            onClick={() => handleCancelTravelSchedule(data.svNumber)}
                                                        >
                                                            <Cancel sx={{ fontSize: '1.1rem' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={12}>
                                <Typography variant="subtitle1" align="center">
                                    No local travel data available
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            {selectedSvData && openEditSvDataDialog && isUserFromAgency && (
                <UpdateSvData
                    isOpen={openEditSvDataDialog}
                    setIsOpen={setOpenEditSvDataDialog}
                    svData={selectedSvData}
                    agencyName={agencyName}
                    pageNumber={pageNumber}
                    pageSize={pageSize}
                />
            )}
            {selectedSvNumber && openCancelTravelScheduleDialog && isUserFromAgency && (
                <CancelTravelSchedule
                    isOpen={openCancelTravelScheduleDialog}
                    setIsOpen={setOpenCancelTravelScheduleDialog}
                    svNumber={selectedSvNumber}
                    intermediary={agencyName}
                    page={pageNumber}
                    pageSize={pageSize}
                />
            )}
        </TableContainer>
    );
};
